import React, { useEffect, useState } from "react";
import { getAuth, onAuthStateChanged, sendEmailVerification } from "firebase/auth";
import { toast } from "react-toastify";

// PT-EMAILVERIFY v1: members can log in without verifying their email.
// Until they verify, this strip reminds them and lets them resend the mail.
const usesEmailPassword = (u) => !!u && Array.isArray(u.providerData) && u.providerData.some((p) => p && p.providerId === "password");

function EmailVerifyBanner() {
    const [user, setUser] = useState(null);
    const [verified, setVerified] = useState(true);
    const [busy, setBusy] = useState(false);
    const [cooldown, setCooldown] = useState(0);

    useEffect(() => {
        const auth = getAuth();
        const unsub = onAuthStateChanged(auth, (u) => {
            setUser(u || null);
            setVerified(!u || !usesEmailPassword(u) || !!u.emailVerified);
        });
        return () => unsub && unsub();
    }, []);

    // when the member comes back to the tab (after clicking the link in the mail), check again
    useEffect(() => {
        if (!user || verified) return undefined;
        const recheck = () => {
            if (document.visibilityState === "hidden") return;
            user.reload()
                .then(() => {
                    const u = getAuth().currentUser;
                    if (u && u.emailVerified) setVerified(true);
                })
                .catch(() => {});
        };
        window.addEventListener("focus", recheck);
        document.addEventListener("visibilitychange", recheck);
        return () => {
            window.removeEventListener("focus", recheck);
            document.removeEventListener("visibilitychange", recheck);
        };
    }, [user, verified]);

    useEffect(() => {
        if (cooldown <= 0) return undefined;
        const t = setTimeout(() => setCooldown(cooldown - 1), 1000);
        return () => clearTimeout(t);
    }, [cooldown]);

    if (!user || verified) return null;

    const resend = async () => {
        if (busy || cooldown > 0) return;
        setBusy(true);
        try {
            await sendEmailVerification(user);
            toast.success("Verification email sent to " + (user.email || "your email") + ". Please check inbox and spam.");
            setCooldown(60);
        } catch (error) {
            if (error && error.code === "auth/too-many-requests") {
                toast.error("Too many requests. Please try again after some time.");
                setCooldown(60);
            } else {
                toast.error("Could not send the email. Please try again.");
            }
        } finally {
            setBusy(false);
        }
    };

    const checkNow = async () => {
        try {
            await user.reload();
            const u = getAuth().currentUser;
            if (u && u.emailVerified) {
                setVerified(true);
                toast.success("Email verified. Thank you!");
            } else {
                toast.info("Not verified yet. Please open the link in the email we sent.");
            }
        } catch (e) {
            toast.error("Could not check right now. Please try again.");
        }
    };

    const btn = {
        background: "#fff",
        border: "1px solid #c77700",
        color: "#8a4b00",
        borderRadius: "6px",
        padding: "4px 10px",
        fontSize: "13px",
        lineHeight: 1.4,
        cursor: "pointer",
        whiteSpace: "nowrap",
    };

    return (
        <div role="status" className="pt-email-verify-banner" style={{ background: "#fff4d6", borderBottom: "1px solid #f0c36d", color: "#5c3b00", fontSize: "14px" }}>
            <div className="container" style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "center", gap: "8px 12px", padding: "8px 12px", textAlign: "center" }}>
                <span style={{ minWidth: 0, overflowWrap: "anywhere" }}>
                    Please verify your email <strong>{user.email}</strong> — this keeps your account and password reset safe.
                </span>
                <span style={{ display: "inline-flex", gap: "8px", flexWrap: "wrap", justifyContent: "center" }}>
                    <button type="button" style={{ ...btn, opacity: busy || cooldown > 0 ? 0.6 : 1 }} onClick={resend} disabled={busy || cooldown > 0}>
                        {busy ? "Sending..." : cooldown > 0 ? `Resend in ${cooldown}s` : "Resend email"}
                    </button>
                    <button type="button" style={btn} onClick={checkNow}>
                        I have verified
                    </button>
                </span>
            </div>
        </div>
    );
}

export default EmailVerifyBanner;
